import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { StatCard } from "./StatCard";

// Constants
const SCROLL_BY_AMOUNT = 400;
const SKELETON_COUNT = 9;
const SCROLL_CHECK_DELAY = 100;
const STAT_CARD_WIDTH = 24;
const STAT_CARD_HEIGHT = 14;

interface FeedMetric {
  label: string;
  value: string | number;
  color?: string;
  tooltip?: string;
}

interface FeedMetricsRendererProps {
  stats?: FeedMetric[];
  isLoading?: boolean;
}

const tooltipContent: Record<string, string> = {
  "Total Mentions":
    "The total number of articles based on the selected date ranges and filters.",
  "Net LLM Impact Score":
    "The total LLM Impact Score of all articles combined — positive scores minus negative scores.",
  "Average Article Impact":
    "The average impact per article, on a scale of -100 to 100, showing if coverage leans more positive or negative.",
  "Average Impact Strength":
    "The average intensity of each article, regardless of whether it's positive or negative, on a scale of 1 to 100.",
  Readership:
    "The total estimated audience size of all articles, using SimilarWeb's deduplicated unique visitors per month for each publication.",
  "Social Engagements":
    "The total likes, shares, and reactions across Facebook, X, Reddit, and Pinterest.",
  "Net Sentiment":
    "Sentiment based on the impact of positive and negative coverage relative to overall gross impact.",
  "Gross Impact":
    "The combined strength of all articles, ignoring whether they're positive or negative.",
  "Gross Positive Impact": "The total strength of all positive articles.",
  "Gross Neutral Impact": "The total strength of all neutral articles.",
  "Gross Negative Impact": "The total strength of all negative articles.",
  "Tier 1":
    "Total articles in top-tier publications, based on readership, domain authority, or manual assignment.",
  "Tier 2":
    "Total articles in mid-tier publications, based on readership, domain authority, or manual assignment.",
  "Tier 3":
    "Total articles in lower-tier publications, based on readership, domain authority, or manual assignment.",
  Headlines:
    "Articles where the brand or topic of interest appears in the headline — the highest level of prominence.",
  Features:
    "Articles where the brand or topic of interest is a main focus in the story (mentioned at least 3 times), though not in the headline.",
  "Passing Mentions":
    "Articles where the brand or topic of interest is only mentioned once or twice and not in the headline.",
  "Positive Mentions": "The total count of articles with positive sentiment.",
  "Neutral Mentions": "The total count of articles with neutral sentiment.",
  "Negative Mentions": "The total count of articles with negative sentiment.",
};

/**
 * FeedMetricsRenderer component displays a horizontal scrolling list of statistics
 */
export const FeedMetricsRenderer = ({
  stats = [],
  isLoading = false,
}: FeedMetricsRendererProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({
      left: -SCROLL_BY_AMOUNT,
      behavior: "smooth",
    });
  }, []);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: SCROLL_BY_AMOUNT, behavior: "smooth" });
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Check scroll state on mount and when stats change
  const checkInitialScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const renderScrollButton = useCallback(
    (direction: "left" | "right") => {
      const buttonIcon =
        direction === "left" ? (
          <CaretLeftIcon className="w-5 h-5 text-gray-600" />
        ) : (
          <CaretRightIcon className="w-5 h-5 text-gray-600" />
        );
      const buttonClick = direction === "left" ? scrollLeft : scrollRight;

      return (
        <>
          {/* Fade overlay */}
          <div
            className={cn(
              "absolute top-0 h-full w-24 from-white via-white/80 to-transparent pointer-events-none z-1",
              `${direction}-0`,
              `${direction === "left" ? "bg-linear-to-r" : "bg-linear-to-l"}`,
            )}
          />
          {/* Background wall */}
          <div
            className={cn(
              "absolute top-0 h-full w-8 bg-white pointer-events-none z-1",
              `${direction}-0`,
            )}
          />
          <IconButton
            icon={buttonIcon}
            variant="outline"
            size="md"
            onPress={buttonClick}
            aria-label={direction === "left" ? "Scroll left" : "Scroll right"}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 shadow-md shadow-gray-100 border-gray-100 bg-white hover:bg-gray-100 hover:border-gray-100 z-2",
              `${direction}-0`,
            )}
          />
        </>
      );
    },
    [scrollLeft, scrollRight],
  );

  const renderSkeleton = useCallback((index: number) => {
    return (
      <div
        key={`feed-metric-skeleton-${index}`}
        className={cn(
          "flex flex-col justify-between border border-gray-100 rounded-[10px] py-1 px-2 shrink-0",
          `w-${STAT_CARD_WIDTH} h-${STAT_CARD_HEIGHT}`,
        )}
      >
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-5 w-10" />
      </div>
    );
  }, []);

  const renderStats = useCallback(() => {
    if (isLoading) {
      return Array.from({ length: SKELETON_COUNT }, (_, i) =>
        renderSkeleton(i),
      );
    }

    return stats.map((stat) => (
      <StatCard
        key={`feed-metric-${stat.label}`}
        label={stat.label}
        value={stat.value}
        color={stat.color}
        tooltip={tooltipContent[stat.label]}
      />
    ));
  }, [stats, isLoading, renderSkeleton]);

  // Reset scroll position when stats change
  useEffect(() => {
    // Reset scroll position to beginning when stats change
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [stats, isLoading]);

  // Only check scroll state when NOT loading (prevents buttons from appearing during loading)
  useEffect(() => {
    if (isLoading) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    // Use a small timeout to ensure DOM is rendered
    const timeoutId = setTimeout(() => {
      checkInitialScroll();
    }, SCROLL_CHECK_DELAY);

    el.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      clearTimeout(timeoutId);
      if (el) {
        el.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", handleScroll);
    };
  }, [isLoading, checkInitialScroll, handleScroll]);

  return (
    <div className="relative flex items-center overflow-hidden">
      {canScrollLeft && renderScrollButton("left")}

      {/* Stats List */}
      <div
        ref={scrollRef}
        className="flex flex-row gap-1 overflow-x-auto overflow-y-hidden scroll-smooth no-scrollbar h-[56px] min-w-full"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {renderStats()}
      </div>

      {canScrollRight && renderScrollButton("right")}
    </div>
  );
};
